import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Heart, Users, Zap, ChevronRight, Check, Loader2, RefreshCw } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function SkillIcon({ iconName, className }) {
  if (iconName === 'Heart') return <Heart className={className} />;
  if (iconName === 'Users') return <Users className={className} />;
  if (iconName === 'Zap') return <Zap className={className} />;
  return <Heart className={className} />;
}

function GuidedExercise({ exercise, onComplete }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [reflection, setReflection] = useState('');
  const [completed, setCompleted] = useState(false);

  const handleNext = () => {
    if (currentStep < exercise.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      setCompleted(true);
    }
  };

  const handleSubmit = () => {
    onComplete(reflection);
  };

  if (completed) {
    return (
      <div className="space-y-4">
        <div className="card-soft bg-secondary/50">
          <p className="font-medium text-foreground mb-2">{exercise.reflection}</p>
          <Textarea
            value={reflection}
            onChange={(e) => setReflection(e.target.value)}
            placeholder="Take a moment to reflect..."
            className="min-h-[100px]"
            data-testid="reflection-input"
          />
        </div>
        <Button onClick={handleSubmit} className="w-full btn-primary" data-testid="complete-exercise-btn">
          <Check className="h-5 w-5 mr-2" />Complete Exercise
        </Button>
      </div>
    );
  }

  const stepCount = exercise.steps ? exercise.steps.length : 0;

  return (
    <div className="space-y-6">
      <div className="card-soft">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-muted-foreground">Step {currentStep + 1} of {stepCount}</span>
          <div className="flex gap-1">
            {exercise.steps && exercise.steps.map((_, i) => (
              <div key={i} className={`w-2 h-2 rounded-full ${i <= currentStep ? 'bg-primary' : 'bg-secondary'}`} />
            ))}
          </div>
        </div>
        <p className="text-lg font-medium text-foreground text-center py-8" data-testid="step-content">
          {exercise.steps && exercise.steps[currentStep]}
        </p>
      </div>
      <Button onClick={handleNext} className="w-full btn-primary" data-testid="next-step-btn">
        {currentStep < stepCount - 1 ? 'Next' : 'Finish'} <ChevronRight className="h-5 w-5 ml-1" />
      </Button>
    </div>
  );
}

function PracticeExercise({ exercise, skillType, onComplete, token }) {
  const [response, setResponse] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleGetFeedback = async () => {
    if (!response.trim()) {
      toast.error('Please write a response first');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/skills/feedback`, null, {
        params: { skill_type: skillType, user_response: response, scenario: exercise.scenario },
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeedback(res.data.feedback);
    } catch (err) {
      setFeedback('Great effort! Keep practicing - every attempt builds your skills.');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onComplete(response);
  };

  return (
    <div className="space-y-4">
      <div className="card-soft bg-secondary/50">
        <p className="text-sm text-muted-foreground mb-2">Scenario:</p>
        <p className="font-medium text-foreground" data-testid="scenario-text">{exercise.scenario}</p>
      </div>

      <div className="card-soft">
        <p className="text-sm font-medium text-foreground mb-2">{exercise.prompt}</p>
        <Textarea
          value={response}
          onChange={(e) => setResponse(e.target.value)}
          placeholder="Type your response..."
          className="min-h-[120px]"
          data-testid="practice-response-input"
        />
        <p className="text-xs text-muted-foreground mt-2">{exercise.example}</p>
      </div>

      {!feedback ? (
        <Button onClick={handleGetFeedback} disabled={loading} className="w-full btn-secondary" data-testid="get-feedback-btn">
          {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : null}
          Get AI Feedback
        </Button>
      ) : (
        <div className="card-soft bg-primary/5 border-primary/20" data-testid="ai-feedback">
          <p className="text-sm font-medium text-foreground mb-2">Feedback:</p>
          <p className="text-sm text-muted-foreground">{feedback}</p>
        </div>
      )}

      <Button onClick={handleComplete} className="w-full btn-primary" data-testid="complete-practice-btn">
        <Check className="h-5 w-5 mr-2" />Complete Exercise
      </Button>
    </div>
  );
}

export default function SkillExercise() {
  const { skillId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [skillData, setSkillData] = useState(null);
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/data/skillExercises.json')
      .then(r => r.json())
      .then(data => {
        setSkillData(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  const skill = skillData ? skillData[skillId] : null;
  
  if (!skill) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground">Skill not found</p>
          <Button onClick={() => navigate('/skills')} className="mt-4" data-testid="back-to-skills-btn">Back to Skills</Button>
        </div>
      </div>
    );
  }

  const exercisesList = skill.exercises || [];
  const currentExercise = exercisesList[currentExerciseIndex];
  const exerciseCount = exercisesList.length;

  const handleExerciseComplete = async (response) => {
    setSaving(true);
    try {
      await axios.post(`${API}/skills/progress`, {
        skill_id: skillId,
        exercise_id: currentExercise.id,
        completed: true,
        feedback: response
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      if (currentExerciseIndex < exerciseCount - 1) {
        setCurrentExerciseIndex(currentExerciseIndex + 1);
        toast.success('Exercise completed! +1');
      } else {
        setCompleted(true);
        toast.success('All exercises completed!');
      }
    } catch (err) {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleNextExercise = () => {
    if (currentExerciseIndex < exerciseCount - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
    }
  };

  if (completed) {
    return (
      <div className="min-h-screen pb-safe" data-testid="session-complete">
        <div className="max-w-lg mx-auto px-6 pt-8">
          <div className="text-center py-12">
            <div className={`inline-flex p-4 rounded-full ${skill.bgColor} mb-4`}>
              <SkillIcon iconName={skill.icon} className={`h-12 w-12 ${skill.color}`} />
            </div>
            <h1 className="font-heading text-2xl font-semibold text-foreground">Session Complete!</h1>
            <p className="text-muted-foreground mt-2">You practiced all {exerciseCount} exercises</p>
            
            <div className="mt-8 space-y-3">
              <Button onClick={() => { setCurrentExerciseIndex(0); setCompleted(false); }} className="w-full btn-secondary" data-testid="practice-again-btn">
                <RefreshCw className="h-5 w-5 mr-2" />Practice Again
              </Button>
              <Button onClick={() => navigate('/skills')} className="w-full btn-primary" data-testid="back-to-hub-btn">
                Back to Skills Hub
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!currentExercise) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">No exercises found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-safe" data-testid="skill-exercise-page">
      <div className="max-w-lg mx-auto px-6 pt-8">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/skills')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" /><span>Back</span>
          </button>
          <span className="text-sm text-muted-foreground" data-testid="progress-indicator">
            {currentExerciseIndex + 1} / {exerciseCount}
          </span>
        </div>

        <div className="mt-6 page-enter">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-xl ${skill.bgColor}`}>
              <SkillIcon iconName={skill.icon} className={`h-5 w-5 ${skill.color}`} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{skill.name}</p>
              <h1 className="font-heading text-2xl font-semibold text-foreground" data-testid="exercise-title">{currentExercise.name}</h1>
            </div>
          </div>
          <p className="mt-3 text-muted-foreground">{currentExercise.description}</p>
        </div>

        <div className="mt-6">
          {currentExercise.type === 'guided' ? (
            <GuidedExercise exercise={currentExercise} onComplete={handleExerciseComplete} />
          ) : (
            <PracticeExercise exercise={currentExercise} skillType={skillId} onComplete={handleExerciseComplete} token={token} />
          )}
        </div>

        <div className="mt-6 text-center">
          <button 
            onClick={handleNextExercise} 
            disabled={currentExerciseIndex >= exerciseCount - 1}
            className="text-sm text-muted-foreground hover:text-foreground disabled:opacity-30"
            data-testid="skip-exercise-btn"
          >
            Skip this exercise
          </button>
        </div>
      </div>
    </div>
  );
}
